import { createError } from '../../../../../lib/error';
import {
  claimsModalEditingModes,
  claimsModalCompletionCodes,

} from './claimsModal.constants';
import {
  normalizeIdentifier,
  castToBoolean,
} from "../../../../../lib/util";

const positionFieldsToRestoreOnCancel = [
  'start_time',
  'end_time',
  'actual_start',
  'actual_end',
  'shiftSegment',
  'meal_break_start',
  'meal_break_end',
  'rest_break_start',
  'rest_break_end',
  'shift_segments_id'

];

const claimFieldsToRestoreOnCancel = [
    'routeSegment',
    'route_segments_id',
    'type',
    'claim_types_id'
];

class ClaimsModalController {
  constructor(
    $scope,
    $uibModalInstance,
    $translate,
    LogService,
    ClaimService,
    PartialClaimService,
    APIService,
    TaskQueueService,
    DataSyncService,
    PositionService,
    showConfirmModal,
    $q,
    DEBUG,
    position,
    staffUser,
    primaryClaim,
    secondaryClaims,
    event,
    editingMode,
    modalClass
  ) {
    "ngInject";
    this.$scope = $scope;
    this.$uibModalInstance = $uibModalInstance;
    this.$translate = $translate;
    this.LogService = LogService;
    this.ClaimService = ClaimService;
    this.PartialClaimService = PartialClaimService;
    this.APIService = APIService;
    this.TaskQueueService = TaskQueueService;
    this.DataSyncService = DataSyncService;
    this.PositionService = PositionService;
    this.showConfirmModal = showConfirmModal;
    this.$q = $q;
    this.DEBUG = DEBUG;
    this.position = position;
    this.staffUser = staffUser;
    this.primaryClaim = primaryClaim;
    this.secondaryClaims = secondaryClaims;
    this.event = event;
    this.editingMode = editingMode;
    this.modalClass = modalClass;

    this._errors = [];
    this._savingClaims = false;
    this._enqueuedClaim = false;
    this._numberOfRemovedSecondaryClaims = 0;

    this._collapserStatus = {};
    this._allCollapserIds = [];

    this._autoSaveSecondaryClaimHandlers = [];
    this._savingPartialClaim = false;

    this._positionValuesForRestore = {};
    this._primaryClaimValuesForRestore = {};

    this.$scope.$on('primaryShiftSegmentUpdated', () => {
      this.$scope.$broadcast('clearSecondaryShiftSegments');
    });
  }

  $onInit() {
    this._numberOfRemovedSecondaryClaims = 0;

    return this._initClaims()
      .catch(err => {
        this.LogService.error("Error with initClaims():", err);
        return this.$q.reject(err);
      })
  }

  /***********************************
   * Editing modes and permissions
   ***********************************/

  set editingMode(mode) {
    this._editingMode = mode;
  }

  get editingMode() {
    return this._editingMode;
  }

  get isEditingModeNew() {
    return this.editingMode === claimsModalEditingModes.new;
  }

  get isEditingModeResubmit() {
    return this.editingMode === claimsModalEditingModes.resubmit;
  }

  get isEditingModeEditPending() {
    return this.editingMode === claimsModalEditingModes.editPending;
  }

  get isEditingModeContinuingClaim() {
    return this.editingMode === claimsModalEditingModes.continuingClaim;
  }

  canEditPrimaryClaim(claim) {
    return this.canSavePrimaryClaim(claim);
  }

  canSavePrimaryClaim(claim) {
    let canSave;
    if (this.isEditingModeResubmit) {
      // Can always save claim in resubmit mode
      canSave = true;
    } else {
      if (this.position && this.position.canSubmitPrimaryClaim) {
        // Don't allow if claim is: approved, declined, or declined final
        if (claim.isApproved || claim.isDeclined || claim.isDeclinedFinal) {
          canSave = false;
        } else if (claim.isSubmitted || claim.enqueued) {
          // Per MOSAICPT-388, also don't allow save/edit when claim is submitted
          canSave = false;
        } else if (claim.isPaid) {
          // Per MOSAICPT-679, don't allow save/edit when claim is already paid
          canSave = false;
        } else {
          canSave = true;
        }
      } else {
        canSave = false;
      }
    }
    return canSave;
  }

  get canCreateSecondaryClaim() {
    let canCreate;
    if (this.isEditingModeResubmit) {
      // Never allow creation of secondary claims in resubmit mode
      canCreate = false;
    } else {
      // For new and pending editing modes, allow based on position config
      canCreate = this.position && this.position.canSubmitSecondaryClaims;
    }
    return canCreate;
  }

  canEditSecondaryClaim(claim) {
    return this.canSaveSecondaryClaim(claim);
  }

  canSaveSecondaryClaim(claim) {
    let canSave;
    if (this.isEditingModeResubmit) {
      // Can always save claim in resubmit mode
      canSave = true;
    } else {
      // Can save secondary claims that are not: approved, declined, or declined final
      if (claim.isApproved || claim.isDeclined || claim.isDeclinedFinal) {
        canSave = false;
      } else if (claim.isSubmitted || claim.enqueued) {
        // Per MOSAICPT-388, also don't allow save/edit when claim is submitted
      } else if (claim.isPaid) {
        // Per MOSAICPT-679, don't allow save/edit when claim is already paid
        canSave = false;
      } else {
        canSave = true;
      }
    }
    return canSave;
  }

  get canSubmitPrimaryClaimsForOtherUsers() {
    return this.position && this.position.canSubmitPrimaryClaimsForOtherUsers;
  }

  canEditPrimaryClaimForOtherUsersPosition(position) {
    return this.canSavePrimaryClaimForOtherUsersPosition(position);
  }

  canSavePrimaryClaimForOtherUsersPosition(position) {
    let allowed = false;
    if (this.canSubmitPrimaryClaimsForOtherUsers) {
      // Only allow if claim has NOT been synced
      if (position.primaryClaim && !position.primaryClaim.isSynced) {
        allowed = true;
      }
    }
    return allowed;
  }

  _pushError(err) {
    this._errors.push(err);
  }

  _clearErrors() {
    this._errors = [];
    this.claimAttestationError = false;
  }

  get errors() {
    return this._errors;
  }

  get savingClaims() {
    return this._savingClaims;
  }

  set isOnline(val) {
    this._isOnline = val;
  }

  get isOnline() {
    if (this.APIService) {
      return this.APIService.isOnline;
    }
    return false;
  }

  set primaryClaim(claim) {
    this._primaryClaim = claim;
  }

  get primaryClaim() {
    return this._primaryClaim;
  }

  get position() {
    return this._position;
  }

  set position(position) {
    if (this._position === undefined) {
      // Initial load; don't reload claims
      this._position = position;
    } else if (this._position !== position) {
      // Not initial load; reload claims
      this._position = position;
      this._initClaims()
        .catch(err => {
          this.LogService.error("Error with initClaims() after setting position to", position, "error:", err);
          return this.$q.reject(err);
        })
    }
  }

  set event(event) {
    this._event = event;
  }

  get event() {
    this.staffUser._updateRouteSegmentsByDate();
    this._event.used_claim_type_ids = this.usedClaimTypeIds;
    this._event.used_segment_ids = this.usedSegmentIds;
    return this._event;
  }

  get staffUser() {
    return this._staffUser;
  }

  get usedClaimTypeIds() {
      let used_claim_type_ids = [];
      if (this.primaryClaim && this.primaryClaim.type) {
        let id = parseInt(this.primaryClaim.type.id);
        if (!isNaN(id)) {
          used_claim_type_ids.push(id);
        }
      }

      if (this.position && this.position.editingSecondaryClaim && this.position.editingSecondaryClaim.type) {
        const id = parseInt(this.position.editingSecondaryClaim.type.id);
        if (!isNaN(id)) {
          /**
           * Temporarily disabling this. The intent here is not not prevent multiple secondary claims
           * of the same type, and to only prevent them from adding a secondary claim of the same type
           * as the primary claim, which is generally something like "Event Hours" or "Event Time".
           * Also see below around line 331 where secondary claims are iterated over. It was also disabled
           * there as well. CORE1-5322
           */
          //used_claim_type_ids.push(id);
        }

        /**
         * If we are editing a secondary claim, we don't have direct access to the primaryClaim info.
         * Because of that, we're going to grab it from the position data instead, but ONLY if we
         * are editing a secondary claim.
         */
        if (this._position.claim_types_id) {
          const secondaryClaimTypeId = parseInt(this._position.claim_types_id);
          if (!isNaN(secondaryClaimTypeId)) {
            used_claim_type_ids.push(secondaryClaimTypeId);
          }
        }
      }

      if (this.secondaryClaims) {
        angular.forEach(
          this.secondaryClaims,
          function(claim, key){
            if (claim.type) {
              let id = parseInt(claim.type.id);
              if (!isNaN(id)) {
                /**
                 * Disabled per CORE1-5322. See above for more details.
                 */
                //this.push(id);
              }
            }
          },
          used_claim_type_ids
        );
      }
      return used_claim_type_ids;
  }

    get usedSegmentIds() {
      const used_segment_ids = [];
      if (this.primaryClaim && this.primaryClaim.routeSegment) {
        const id = parseInt(this.primaryClaim.routeSegment.id);
        if (!isNaN(id)) {
          used_segment_ids.push(id);
        }
      }

      if (
        this.position
        && this.position.editingSecondaryClaim
        && this.position.editingSecondaryClaim.routeSegment
      ) {
        const id = parseInt(this.position.editingSecondaryClaim.routeSegment.id);
        if (!isNaN(id)) {
          used_segment_ids.push(id);
        }
      }

      if (this.secondaryClaims) {
        angular.forEach(
          this.secondaryClaims,
          function(claim,key){
            if (claim.routeSegment)
            {
              let id = parseInt(claim.routeSegment.id);
              if (!isNaN(id)) {
                this.push(id);
              }
            }
          },
          used_segment_ids
        );
      }
      return used_segment_ids;
    }

  set staffUser(staffUser) {
    if (this._staffUser === undefined) {
      // Initial load; don't reload claims
      this._staffUser = staffUser;
    } else if (this._staffUser !== staffUser) {
      // Not initial load; reload claims
      this._staffUser = staffUser;
      this._initClaims()
        .catch(err => {
          this.LogService.error("Error with initClaims() after setting staffUser to", staffUser, "error:", err);
          return this.$q.reject(err);
        });
    }
  }

  get secondaryClaims() {
    return this._secondaryClaims;
  }

  get secondaryClaimTypes(){
    const claims = this._secondaryClaims;
    const type_ids = [];
    if (claims) {
      for (let i = 0; i <= claims.length; i++) {
        type_ids.push(claims[i].claim_types_id);
      }
    }
    return type_ids;
  }

  set secondaryClaims(claims) {
    this._secondaryClaims = claims;
  }

  get otherUsersPositions() {
    return this.position && this.position.other_users_positions || [];
  }

  get canClose() {
    if (this.usePromptRecapCheckoutClaim) return false
    return true;
  }

  get canSaveAndExit() {
    if (this.usePromptRecapCheckoutClaim) return false
    return this.canClose && !this.isEditingModeResubmit;
  }

  get usePromptRecapCheckoutClaim() {
    return this.position && this.position.event && !!this.position.event.prompt_recap_checkout_claim
  }

  get showClose() {
    return this.canClose && !this.canSaveAndExit;
  }

  get showCloseWithoutSaving() {
    return this.canSaveAndExit;
  }

  get showSaveAndExit() {
    return this.canSaveAndExit;
  }

  get submitButtonEnabled() {
    return !!this.allExceptionsAcknowledged();
  }

  get debug() {
    return this.DEBUG;
  }

  set secondaryClaimEditor(ctrl) {
    this._secondaryClaimEditor = ctrl;
  }

  get secondaryClaimEditor() {
    return this._secondaryClaimEditor;
  }

  _doClose(completionCode = null) {
    return this.$q.when(this.$uibModalInstance.close(completionCode))
      .then(() => {
        const params = {
          completionCode,
          positionId: this.position && this.position.id,
        };
        this.LogService.debug(`ClaimsModal _doClose() finished. Emitting ClaimsModalClosed with params`, params);
        this.$scope.$emit('claimsModalClosed', params);
      });
  }

  closeAfterSavePartial() {
    const completionCode = claimsModalCompletionCodes.savedPartial;
    return this._doClose(completionCode);
  }

  closeWithoutSaving() {
    return this.$translate('CONFIRM_CLOSE_CLAIMS_MODAL')
      .then(message => this.showConfirmModal(message))
      .then(() => {
        const completionCode = claimsModalCompletionCodes.cancelled;
        this._restoreInitialPositionValues();
        return this._doClose(completionCode);
      });
  }

  cancel() {
    return this.closeWithoutSaving();
  }

  get primaryClaimDescription() {
    return this.describePrimaryClaim(this.position, this.staffUser)
  }

  otherUsersPrimaryClaimDescription(position) {
    return this.describePrimaryClaim(position);
  }

  getTranslation(key) {
    if (!this._translations) {
      this._translations = {};
    }
    if (!this._translations[key]) {
      this._translations[key] = key;
      this.$translate(key)
        .then(translated => {
          this._translations[key] = translated;
        });
    }
    return this._translations[key];
  }

  describePrimaryClaim(position, staffUser) {
    const parts = [];
    if (staffUser) {
      parts.push(staffUser.first_name + ' ' + staffUser.last_name);
    } else if (position.staffing_users_first_name || position.staffing_users_last_name) {
      parts.push(position.staffing_users_first_name + ' ' + position.staffing_users_last_name);
    }
    if (position) {
      parts.push(this.PositionService.translatedNameForPosition(position));
    } else {
      this.LogService && this.LogService.warn(`ClaimsModalController.describePrimaryClaim() called with empty position`);
    }
    parts.push(this.getTranslation('CLAIM_TYPE') + ' & ' + this.getTranslation('TIME'));
    return parts.join(' - ');
  }

  describeSecondaryClaim(claim) {
    return this.ClaimService.descriptiveTitleForClaim(claim);
  }

  addAutoSaveSecondaryClaimHandler(handler) {
    this._autoSaveSecondaryClaimHandlers.push(handler);
  }

  removeAutoSaveSecondaryClaimHandler(handler) {
    const idx = this._autoSaveSecondaryClaimHandlers.indexOf(handler);
    if (idx >= 0) {
      this._autoSaveSecondaryClaimHandlers.splice(idx, 1);
    }
  }

  validate() {
    const parentForm = this.claimsForm;
    this._clearErrors();

    const valid = parentForm && parentForm.$valid;
    if (!valid) {
      return this.$q.reject(createError({ error: 'enterAllRequiredFields', message: 'Please enter a value for all required fields.' }));
    }

    if (this.requireClaimAttestation && this.claimAttestationInputEnabled && !this.claimAttestationCheckboxChecked) {
      this.claimAttestationError = true;
      return this.$translate('CLAIM_ATTESTATION_MISSING')
        .then(message => this.$q.reject(createError({
          error: 'claimAttestationRequired',
          message,
        })));
    }

    return true;
  }

  get isSavingPartialClaim() {
    return this._savingPartialClaim;
  }

  get claimsForm() {
    return this.$scope && this.$scope.claimsForm;
  }

  get isPristine() {
    return this.claimsForm && this.claimsForm.$pristine;
  }

  savePartialClaimAndExit() {
    const eventId = (this.event && this.event.id) || (this.position && this.position.event && this.position.event.id);
    const staffUserId = this.staffUser && this.staffUser.id;

    this._savingPartialClaim = true;
    return this.$q
      .all([
        this._autoSaveSecondaryClaims(false),
      ])
      .then(() => {
        // Create partial claims
        const primaryClaim = this.canEditPrimaryClaim(this.primaryClaim) ? this.primaryClaim : null;
        const secondaryClaims = this.secondaryClaims
          && this.secondaryClaims.filter(claim => this.canEditSecondaryClaim(claim))
          || [];
        if (!this.isPristine || this._numberOfRemovedSecondaryClaims || primaryClaim || secondaryClaims.length > 0) {
          return this.PartialClaimService.createFromClaims(primaryClaim, secondaryClaims, eventId, staffUserId, this.position);
        } else {
          this.LogService.warn(`ClaimsModal savePartialClaimAndExit() no editable claims found, and form is pristine. Not saving partial claim.`);
          return null;
        }
      })
      .then(partialClaim => {
        if (!partialClaim) {
          return this.closeAfterSavePartial();
        }

        /**
         * Set up PartialClaimSaveTask with all relevant info
         */
        const task = this.TaskQueueService.createTask('PartialClaimSaveTask');
        task.partialClaim = partialClaim;

        let saveOrEnqueuePromise = null;
        if (task.canExecute) {
          saveOrEnqueuePromise = task.execute()
            .then(result => {
              this.LogService.debug(`ClaimsModalController.savePartialClaimAndExit() executed save task with result:`, result);
              return result;
            })
        } else {
          saveOrEnqueuePromise = this.TaskQueueService.enqueue(task)
            .then(result => {
              this.LogService.debug(`ClaimsModalController.savePartialClaimAndExit() enqueued task with result:`, result);
              return result;
            })
        }

        return saveOrEnqueuePromise
          .then(() => {
            /**
             * Update staffUser with PartialClaim
             */
            this.LogService.debug(`ClaimsModal savePartialClaimAndExit() saved or enqueued; now updating staffUser with partial claim. Current staffUser.partialClaims.length=${this.staffUser.partialClaims.length}`);
            this.PartialClaimService.updateStaffUserPartialClaimsForPosition(this.staffUser, this.position, partialClaim);
            this.LogService.debug(`ClaimsModal savePartialClaimAndExit() just updated staff user partial claims. now length: ${this.staffUser.partialClaims.length}`);
          })
          .then(() => this.closeAfterSavePartial())
          .catch(err => {
            this.LogService.error(`ClaimsModalController.savePartialClaimAndExit() error saving/enqueuing:`, err);
            return this.$q.reject(err);
          });
      })
      .finally(result => {
        this._savingPartialClaim = false;
        return result;
      });
  }

  submitClaims() {
    return this.$q
      .all([
        this.validate(),
        this._autoSaveSecondaryClaims(),
      ])
      .catch(err => {
        this.LogService.error("Error validating or auto saving secondary claims:", err);
        this._pushError(err);
        return this.$q.reject(err);
      })
      .then(() => {
        return this._saveClaims()
          .then(result => {
            const completionCode = claimsModalCompletionCodes.submitted;
            const closePromise = this.$q.when(this._doClose(completionCode));
            if (result !== null) {
              return closePromise;
            } else {
              return closePromise;
            }
          })
          .catch(err => {
            this.LogService.error("Error saving claims:", err);
            this._pushError(err);
            return this.$q.reject(err);
          })
      });
  }

  /****************************
   * Private methods
   ****************************/

  _initClaims() {
    let promises = [];
    if (!this.staffUser) {
      this.LogService.error("missing staff user");
      return this.$q.reject(createError({
        error: 'MissingStaffUser',
        message: 'Staff user is required for claims',
      }));
    }
    if (!this.event) {
      this.LogService.error("missing event");
      return this.$q.reject(createError({
        error: 'MissingEvent',
        message: 'Event is required for claims',
      }));
    }

    const loadPendingClaims = () => {
      const promises = [];
      // Get existing claims for this event from the user
      let allEventClaims = this.staffUser.getClaimsForEventId(this.position.event.id);

      if (this.position && this.position.enqueuedPrimaryClaim) {
        this.primaryClaim = this.position.enqueuedPrimaryClaim;
      } else {
        const positionId = this.position && this.position.id;
        if (positionId) {
          const primaryClaims = allEventClaims.filter(
            claim =>
              claim.isPrimaryClaim
              && claim.position
              && parseInt(claim.position.id) === parseInt(positionId)
          );
          this.primaryClaim = primaryClaims.shift();
        } else {
          this.LogService.warn(`ClaimsModal _initClaims() don't have position ID so can't find primary claim`);
        }
      }
      if (!this.primaryClaim) {
        promises.push(
          this._getOrCreatePrimaryClaim()
            .then(claim => this.primaryClaim = claim)
        );
      }

      if (this.event && this.event.enqueuedSecondaryClaims && this.event.enqueuedSecondaryClaims.length) {
        this.secondaryClaims = this.event.enqueuedSecondaryClaims;
      } else {
        this.secondaryClaims = allEventClaims.filter(claim => !claim.isPrimaryClaim);
      }

      return this.$q.all(promises);
    }

    if (this.isEditingModeNew) {
      // Create primary claim if not present
      if (!this.primaryClaim) {
        promises.push(
          this._getOrCreatePrimaryClaim()
            .then(claim => this.primaryClaim = claim)
        );
      }

      // Create claims for other users if not present
      if (this.canSubmitPrimaryClaimsForOtherUsers) {
        promises.push(this._populateOtherUsersClaims());
      }

    } else if (this.isEditingModeEditPending) {
      promises.push(loadPendingClaims());
    } else if (this.isEditingModeResubmit) {
      if (!this.primaryClaim && !this.secondaryClaims) {
        this.LogService.error("Resubmit mode specified but no primary or secondary claims present");
      }
    } else if (this.isEditingModeContinuingClaim) {
      promises.push(
        loadPendingClaims()
          .then(() => this.PartialClaimService.getForPosition(this.staffUser, this.position))
          .then(partialClaim => {
            if (partialClaim) {
              if (partialClaim.primaryClaim) {
                this.primaryClaim = partialClaim.primaryClaim;
              }
              partialClaim.secondaryClaims.forEach(secondaryClaim => {
                const existingIdx = this.secondaryClaims.findIndex(existingClaim => normalizeIdentifier(existingClaim.id) === normalizeIdentifier(secondaryClaim.id));
                if (existingIdx >= 0) {
                  // Replace!
                  this.secondaryClaims[existingIdx] = secondaryClaim;
                } else {
                  // Add
                  this.secondaryClaims.push(secondaryClaim);
                }
              });
            } else {
              this.LogService.warn(`_initClaims() did not have partial claim`);
            }
          })
      );
    } else {
      this.LogService.error("Invalid editing mode");
    }

    return this.$q.all(promises)
      .then(() => this._initClaimAttestation())
      .then(() => {
        this._saveInitialPositionValues();
      });
  }

  _initClaimAttestation() {
    // If resubmitting, always remove attestation times from existing claims; we'll overwrite
    if (this.editingMode === claimsModalEditingModes.resubmit) {
      if (this.primaryClaim) {
        this.primaryClaim.attested_at = null;
      }
      if (this.secondaryClaims) {
        this.secondaryClaims.forEach(c => c.attested_at = null);
      }
    }

    // Check if all claims have attestation; if so, re-check the box
    const allClaimsHaveAttestation = this.doAllClaimsHaveAttestedAt();
    /** Per CORE1-6633, we want users to always recheck the claims attestation when editing. */
    const shouldAutoCheckAttestation = false; // Explicitly disable auto-checking for now
    if (allClaimsHaveAttestation && shouldAutoCheckAttestation) {
      this.claimAttestationCheckboxChecked = true;
      this.claimAttestationInputEnabled = false;
    } else {
      this.claimAttestationCheckboxChecked = false;
      this.claimAttestationInputEnabled = true;
    }
  }

  doAllClaimsHaveAttestedAt() {
    const allClaims = [];
    if (this.primaryClaim) {
      allClaims.push(this.primaryClaim);
    }
    if (this.secondaryClaims) {
      this.secondaryClaims.forEach(c => allClaims.push(c));
    }

    return allClaims.every(claim => claim && !!claim.attested_at);
  }

  _saveInitialPositionValues() {
    if (!this.position) {
      return;
    }
    positionFieldsToRestoreOnCancel.forEach(field => {
      this._positionValuesForRestore[field] = this.position[field];
    });

    if (this.primaryClaim) {
      claimFieldsToRestoreOnCancel.forEach(field => {
        this._primaryClaimValuesForRestore[field] = this.primaryClaim[field];
      });
    }
  }

  _restoreInitialPositionValues() {
    if (!this.position) {
      return;
    }
    positionFieldsToRestoreOnCancel.forEach(field => {
      this.position[field] = this._positionValuesForRestore[field];
    });

    if (this.primaryClaim) {
      claimFieldsToRestoreOnCancel.forEach(field => {
        this.primaryClaim[field] = this._primaryClaimValuesForRestore[field];
      });
    }
  }

  _getOrCreatePrimaryClaim() {
    return this.ClaimService.getOrCreatePrimaryClaimForPosition(this.staffUser, this.position)
      .then(claim => {
        return claim;
      })
      .catch(err => {
        this.LogService.warn(`ClaimsModal _getOrCreatePrimaryClaim error:`, err);
        return this.$q.reject(err);
      });
  }

  _loadSecondaryClaims() {
    return this.ClaimService.getSecondaryClaimsForEvent(this.staffUser, this.position.event);
  }

  _populateOtherUsersClaims() {
    const promises = [];
    for (let otherUsersPosition of this.otherUsersPositions) {
      if (!otherUsersPosition.primaryClaim) {
        promises.push(this.ClaimService.createOtherUsersPrimaryClaimForPosition(otherUsersPosition));
      }
    }
    return this.$q.all(promises);
  }

  _autoSaveSecondaryClaims(validate = true) {
    return this.$q.all(this._autoSaveSecondaryClaimHandlers.map(handler => handler(validate)));
  }

  addNewSecondaryClaim(claim) {
    if (!this.secondaryClaims) {
      this.secondaryClaims = [];
    }
    this.secondaryClaims.push(claim);

    // Close claim creation panel
    this._collapserStatus['03_create_secondary'] = true;
  }

  saveSecondaryClaim(claim, replace) {
    const index = this.secondaryClaims.indexOf(replace);
    if (index >= 0) {
      this.secondaryClaims[index] = claim;
    }
    // Note; previously this was used to collapse the new claim, but we no longer need to do that
  }

  removeSecondaryClaim(claim) {
    this._numberOfRemovedSecondaryClaims += 1;

    /** If the claim is synced and has a real id, then we should bail out and never remove it **/
    if (claim.isSynced) {
      return false;
    }

    const idx = this.secondaryClaims.indexOf(claim);
    let promise;
    if (idx >= 0) {
      if (claim.isSynced) {
        const task = this.TaskQueueService.createTask('RemoveSecondaryClaimTask');
        task.setClaim(claim);
        if (task.canExecute) {
          this._savingClaims = true;
          promise = task.execute()
            .then(result => {
              this.LogService.log("Remove claim task completed with result:", result);
            })
            .catch(err => {
              this.LogService.error("Error removing secondary claim:", err);
              return this.$q.reject(err);
            })
            .finally(() => {
              this._savingClaims = false;
            });
        } else {
          promise = this.TaskQueueService.enqueue(task)
            .then(() => {
              this.LogService.log("Enqueue remove claim task:", task);
              return null;
            });
        }
      } else {
        promise = this.$q.when(null);
      }
      promise = promise.then(() => {
        this.secondaryClaims.splice(idx, 1);
      });
    } else {
      this.LogService.warn("Unable to find claim to remove:", claim, "secondaryClaims:", this.secondaryClaims);
      promise = this.$q.reject(createError({
        error: 'UnableToFindClaimToRemove',
        message: 'Unable to find claim to remove',
      }));
    }
    return promise;
  }

  allExceptionsAcknowledged(){
    let all_acknowledged = true;
    if (this.primaryClaim && this.primaryClaim.inExceptionState === true) {
        all_acknowledged = false;
    }
    if (this.position && this.position.editingSecondaryClaim) {
      const claim = this.position.editingSecondaryClaim;
      if (claim.inExceptionState === true && all_acknowledged) {
        all_acknowledged = false
      }
    }

    if (this.requireClaimAttestation && !this.claimAttestationCheckboxChecked) {
      all_acknowledged = false
    }
    return all_acknowledged;
  }


  /*******************************
   * COLLAPSERS
   *******************************/

  isCollapsed(collapserId) {
    let collapsed;
    if (!(collapserId in this._collapserStatus)) {
      collapsed = this.isCollapsedInitially(collapserId);
    } else {
      collapsed = this._collapserStatus[collapserId];
    }
    return collapsed;
  }

  isCollapsedInitially(collapserId) {
    /**
     * Only the first collapser by ID alphabetically should be open initially.
     * To support this, we keep track of each collapser ID we've seen.
     */
    let idx = this._allCollapserIds.indexOf(collapserId);
    if (idx === -1) {
      this._allCollapserIds.push(collapserId);
      this._allCollapserIds.sort();
      idx = this._allCollapserIds.indexOf(collapserId);
    }
    return idx !== 0;
  }

  toggleCollapsed(collapserId) {
    // Toggle specified ID
    this._collapserStatus[collapserId] = !this.isCollapsed(collapserId);

    // Collapse all other panels
    Object.keys(this._collapserStatus).forEach(currCollapserId => {
      if (currCollapserId !== collapserId) {
        this._collapserStatus[currCollapserId] = true;
      }
    });
  }

  _saveClaims() {
    const task = this.TaskQueueService.createTask('ClaimSaveTask');
    let savingPrimaryClaim = null;
    let savingSecondaryClaims = [];
    let savingOtherUsersPrimaryClaims = [];

    const setAttestedAt = this.requireClaimAttestation && this.claimAttestationCheckboxChecked;
    const now = new Date();

    if (this.primaryClaim && this.canSavePrimaryClaim(this.primaryClaim)) {
      this.position.primaryClaim = this.primaryClaim;
      this.position.updateActualHours();
      if (this.position.shiftSegment) {
        this.position.shift_segments_id = this.position.shiftSegment.id;
      }
      if (setAttestedAt && !this.primaryClaim.attested_at) {
        this.primaryClaim.attested_at = now;
      }
      task.addPositionForPrimaryClaim(this.position);
      savingPrimaryClaim = this.primaryClaim;
    }

    // Save secondary claims
    if (this.secondaryClaims) {
      for (const secondaryClaim of this.secondaryClaims) {
        if (this.canSaveSecondaryClaim(secondaryClaim)) {
          if (setAttestedAt && !secondaryClaim.attested_at) {
            secondaryClaim.attested_at = now;
          }
          task.addSecondaryClaim(secondaryClaim);
          savingSecondaryClaims.push(secondaryClaim);
        }
      }
    }

    // Save other users' positions
    if (this.canSubmitPrimaryClaimsForOtherUsers) {
      for (let otherUsersPosition of this.otherUsersPositions) {
        const form = this._formForOtherUsersPosition(otherUsersPosition);
        if (form) {
          if (form.$valid && form.$dirty) {
            task.addPrimaryClaimForOtherUsersPosition(this.staffUser, otherUsersPosition, otherUsersPosition.primaryClaim);
            savingOtherUsersPrimaryClaims.push(otherUsersPosition.primaryClaim);
          }
        }
      }
    }

    if (task.canExecute) {
      this._savingClaims = true;
      const promises = task.execute()
        .then(savedClaims => {
          if (this.DataSyncService.claimsPromise.primaryClaim) {
            this.primaryClaim = this.DataSyncService.claimsPromise.primaryClaim;
          } else {
            this.LogService.warn("No primary claim present in task");
          }

          // Remove partial claims since we were able to submit
          this.PartialClaimService.removeForPosition(this.staffUser, this.position);
          return savedClaims;
        })
        .finally(() => {
          this._savingClaims = false;
          this.DataSyncService.claimsPromise = null;
          this.DataSyncService.sync();
        });
      this.DataSyncService.claimsPromise = this.$q.all(promises);
      return this.DataSyncService.claimsPromise;
    } else {
      // Queue claim now
      this._enqueuedClaim = true;
      const promise = this.TaskQueueService.enqueue(task)
        .then(() => {
          let savingClaims = [];
          if (savingPrimaryClaim) {
            savingClaims.push(savingPrimaryClaim);
            this.position.enqueuedPrimaryClaim = savingPrimaryClaim;
          }
          if (savingSecondaryClaims.length) {
            savingClaims = savingClaims.concat(savingSecondaryClaims);
            this.event.enqueuedSecondaryClaims = savingSecondaryClaims;
          }
          if (savingOtherUsersPrimaryClaims.length) {
            savingClaims = savingClaims.concat(savingOtherUsersPrimaryClaims);
          }

          // Mark claims as enqueued for staff user
          savingClaims.forEach(claim => {
            claim.enqueued = true;
            this.staffUser.addEnqueuedClaimForEventId(claim, this.event.id)
          });

          // Remove partial claim
          this.PartialClaimService.removeForPosition(this.staffUser, this.position);
          return null;
        });
      this.DataSyncService.claimsPromise = promise;
      return promise;
    }
  }

  _formForOtherUsersPosition(position) {
    let parentForm = this.$scope.claimsForm;
    return parentForm[`primaryClaimFormForOtherUser_position_${position.id}`];
  }

  /*
   * Claim Attestation functionality
   */

  get requireClaimAttestation() {
    return castToBoolean(this.staffUser.use_claim_attestation);
  }

  set claimAttestationCheckboxChecked(val) {
    this._claimAttestationCheckboxChecked = val;
  }

  get claimAttestationCheckboxChecked() {
    return this._claimAttestationCheckboxChecked;
  }

  set claimAttestationInputEnabled(val) {
    this._claimAttestationInputEnabled = val;
  }

  get claimAttestationInputEnabled() {
    return this._claimAttestationInputEnabled;
  }

  get claimAttestationLabelKey() {
    // Claims submission: I confirm the claims I have entered accurately reflect all of my time and expenses for this shift.
    // Edit claims: same
    // Edit & resubmit: I confirm the changes I have entered for this claim accurately reflect my shift.

    switch (this.editingMode) {
      case claimsModalEditingModes.resubmit:
        return 'CLAIM_ATTESTATION_MESSAGE_RESUBMIT';
      case claimsModalEditingModes.new:
      case claimsModalEditingModes.editPending:
      case claimsModalEditingModes.continuingClaim:
      default:
        return 'CLAIM_ATTESTATION_MESSAGE';
    }
  }
}

export default ClaimsModalController;
